import { useQueries } from "@tanstack/react-query"
import { reportApi } from "@/services/report-api"
import { queryKeys } from "./query-keys"
import { useAuthReady } from "./use-auth-ready"
import { deserializeParameters } from "@/lib/parameter-serializer"
import type { ReportListItem } from "@/types/report-list"
import type { Report } from "@/types/report"
import type { Parameter } from "@/types/parameter"

interface ItemParameterRequest {
  itemKey: string
  reportPath: string
  parameterValues: Record<string, string>
}

/**
 * Builds a parameter value dictionary from the serialized XML on a ReportListItem.
 * Mirrors the C# ParameterService.GetParametersFromXml logic:
 * deserialize XML entries, then build a dictionary of name -> value(s).
 */
export function buildParameterDictFromXml(xml: string): Record<string, string> {
  const entries = deserializeParameters(xml)
  const dict: Record<string, string> = {}

  for (const entry of entries) {
    if (entry.selectedValues.length === 0) continue
    dict[entry.parameterName] = entry.selectedValues.join(",")
  }

  return dict
}

/**
 * Builds a parameter fetch request for each item that has a resolvable
 * report path. Each item gets its own fetch because the API returns
 * parameters with the stored values applied (e.g., stringValue, boolValue),
 * which differ per item even for the same report.
 */
function buildItemRequests(
  items: ReportListItem[],
  reportPathMap: Map<string, string>,
): ItemParameterRequest[] {
  return items
    .map((item) => {
      const path = item.report?.path || reportPathMap.get(item.reportKey)
      if (!path) return null

      const parameterValues = item.reportListItemParameters
        ? buildParameterDictFromXml(item.reportListItemParameters)
        : {}

      return {
        itemKey: item.reportListItemKey,
        reportPath: path,
        parameterValues,
      }
    })
    .filter((req): req is ItemParameterRequest => req !== null)
}

/**
 * Hook that takes raw ReportListItems and hydrates each item's report
 * with full Parameter objects (including display-friendly `text` fields
 * and typed values like stringValue, boolValue, etc.).
 *
 * This mirrors the Blazor app's LoadParameterDataForReportList behavior:
 * for each item, it calls GetReportParameters with the stored XML values
 * to get back full parameter metadata with values applied.
 *
 * Each item gets its own API call because different items may have
 * different parameter values for the same report. React Query's cache
 * deduplicates identical requests automatically.
 *
 * @param items - Raw report list items from the API
 * @param reportPathMap - Map of reportKey -> report path, used as fallback
 *   when item.report?.path is not populated (common for API-returned items)
 */
export function useHydratedReportListItems(
  items: ReportListItem[],
  reportPathMap: Map<string, string>,
): {
  items: ReportListItem[]
  isLoading: boolean
  isFetching: boolean
} {
  const isAuthReady = useAuthReady()
  const requests = buildItemRequests(items, reportPathMap)

  const queries = useQueries({
    queries: requests.map((req) => ({
      queryKey: queryKeys.reports.parameters(req.reportPath, req.parameterValues),
      queryFn: () =>
        reportApi.getReportParameters(req.reportPath, req.parameterValues),
      enabled: isAuthReady && !!req.reportPath,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    })),
  })

  const isLoading = queries.some((q) => q.isLoading)
  const isFetching = queries.some((q) => q.isFetching)

  // Build a map of itemKey -> Parameter[] from completed queries
  const parametersByItemKey = new Map<string, Parameter[]>()
  for (let i = 0; i < requests.length; i++) {
    const query = queries[i]
    if (query.data) {
      parametersByItemKey.set(requests[i].itemKey, query.data)
    }
  }

  // Hydrate items with parameters
  const hydratedItems = items.map((item) => {
    const parameters = parametersByItemKey.get(item.reportListItemKey)
    if (!parameters) return item

    const path = item.report?.path || reportPathMap.get(item.reportKey)
    return {
      ...item,
      report: item.report
        ? { ...item.report, parameters }
        : { reportKey: item.reportKey, name: item.displayName, path: path || "", tenantId: item.tenantId, parameters } as Report,
    }
  })

  return { items: hydratedItems, isLoading, isFetching }
}
